import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * What an integral adds up — the applications unit of Calculus II.
 *
 * Grade 12 defines the integral as an area, which is the right first definition
 * and a trap by the second week of this course. None of the four quantities in
 * this unit is an area: a length, a surface, an amount of work and a balance
 * point. A student holding "integral = area under a curve" as *the* meaning has
 * to be told four separate formulas here and will remember none of them a month
 * later.
 *
 * So every lesson in this unit is built on the same three-line habit, stated in
 * the same order each time:
 *
 *  1. Cut the thing into slices thin enough that the quantity is *constant*
 *     across each slice.
 *  2. Write down the contribution of one slice, in terms of the slice's
 *     position and its thickness.
 *  3. Integrate it.
 *
 * That is what an integral is for, and the area formula is the case of it where
 * the contribution happens to be f(x) dx. The unit's argument is that the
 * formulas are not worth memorising — the slice is.
 *
 * `arc-length` comes first because its slice contribution is the one nobody
 * guesses. The obvious answer, adding up dx, gives the width of the interval,
 * and it takes a drawing of one hypotenuse to see why the answer has a square
 * root in it. It also earns the techniques unit its keep: √(1 + (f′)²) is
 * almost never integrable in closed form, which is why the handful of curves
 * that do work are the ones every textbook uses.
 *
 * `surface-of-revolution` is a separate concept rather than a section of arc
 * length, because the misconception it exists to catch has a name and a number:
 * students reach for π y², the *disc* they know from volume, instead of 2π y ds.
 * That error produces a plausible figure with no square root in it, and an
 * exercise bank that never asks the question never catches it.
 *
 * `work-integrals` teaches the pumping problem rather than only the spring,
 * even though the spring is easier. Hooke's law gives F(x) and the integral is
 * ∫F dx, which a student can do by pattern-matching; a tank has no F(x) at all
 * until you decide what a slice is, and it is the first problem in this course
 * where the slicing is the entire difficulty.
 *
 * `center-of-mass` closes the unit because it is the only place a student meets
 * a *weighted average* as an integral, and the weighting is what makes ȳ the
 * formula that always looks wrong: half of f, not f.
 */
export const INTEGRAL_APPLICATION_LESSONS: Lesson[] = [
  lesson({
    id: 'arc-length-01',
    unitId: 'unit-integral-applications',
    conceptId: 'arc-length',
    title: 'Arc Length',
    summary: 'How long a curve is, found by adding up the hypotenuses of triangles too small to see.',
    objectives: [
      'Derive the arc-length element ds from Pythagoras on a small piece of curve',
      'Set up and evaluate an arc-length integral for a curve given as y = f(x)',
      'Say why so few curves have an arc length that can be written in closed form',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'How long is a piece of string',
        blocks: [
          text('A straight line from (0, 0) to (3, 4) has length 5, by Pythagoras. A curve from (0, 0) to (3, 4) is longer, and how much longer depends on how it wanders. There is no formula in geometry for this.'),
          text('The idea that works is the one every integral uses: if the curve is straight over a short enough piece, then the length of that piece *is* a hypotenuse, and the total length is what you get by adding up all of them.'),
          insight('This is the first application in the course where the answer is not an area, and it is worth noticing how little that matters. The integral was never really about area — it is about adding up infinitely many small contributions, and area was the first contribution anyone needed.'),
        ],
      },
      intuition: {
        title: 'One small hypotenuse',
        blocks: [
          text('Take a piece of the curve so short it is indistinguishable from a straight segment. It runs dx across and dy up, so by Pythagoras its length ds is'),
          math('ds = \\sqrt{(dx)^{2} + (dy)^{2}}'),
          text('That is symmetric in x and y, and not yet an integral, because it is not in terms of a single variable. Factor dx out of the root:'),
          math('ds = \\sqrt{1 + \\left(\\frac{dy}{dx}\\right)^{2}}\\;dx'),
          text('and now every piece is a function of x times dx, which is exactly what an integral adds up. So for a curve y = f(x) running from x = a to x = b:'),
          math('L = \\int_{a}^{b}\\sqrt{1 + \\big(f\'(x)\\big)^{2}}\\;dx'),
          insight('The 1 under the root is the dx part — the horizontal distance the piece covers. It is why a flat curve, with f′ = 0, has length exactly b − a, and it is the term most often dropped. Dropping it claims that a horizontal line has zero length.'),
          warning('The derivative is squared before the 1 is added, and the root is taken of the whole thing. √(1 + (f′)²) is not 1 + f′, and it is not √1 + √(f′²). These are not the same function and they do not agree anywhere except where f′ = 0.', 'The root covers both terms'),
          text('**Why so few of these come out.** The integrand is a square root of one plus a square, and that shape is almost never something the techniques unit can finish. Even a curve as ordinary as y = x² gives'),
          math('L = \\int \\sqrt{1 + 4x^{2}}\\;dx'),
          text('which needs a trigonometric substitution and produces a logarithm. A parabola is the simplest curve in mathematics after a line, and its arc length is already a hard integral.'),
          tip('The curves that appear in exercises are chosen so that 1 + (f′)² is a perfect square. That is a strong hint about how to work backwards: if a question gives you y = ⅔x^{3/2}, it is because f′ = √x makes 1 + (f′)² equal to 1 + x.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: 'dx', height: 'dy', hypotenuse: 'ds' } },
            'The whole derivation, in one triangle. Over a piece of curve short enough to be straight, the length ds is the hypotenuse of a right triangle with legs dx and dy. Every arc-length formula in mathematics — for y = f(x), for a parametric curve, for a polar one — is this triangle with the two legs written differently.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              min: -2,
              max: 3,
              points: [
                { x: 0, y: 0, label: 'start' },
                { x: 2, y: 4, label: 'end' },
              ],
              label: 'y = x²',
            },
            'The straight distance from (0, 0) to (2, 4) is √20 ≈ 4.47. The curve between them is longer — about 4.65 — because it climbs at a rate that keeps changing. Arc length always exceeds the straight-line distance, which is the plausibility check to run on every answer.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the length of y = ⅔x^{3/2} from x = 0 to x = 3.'),
          steps([
            { label: 'Differentiate.', latex: 'f\'(x) = \\tfrac{2}{3}\\cdot\\tfrac{3}{2}x^{1/2} = x^{1/2}' },
            { label: 'Square it and add 1.', latex: '1 + \\big(f\'(x)\\big)^{2} = 1 + x', note: 'This is why the curve was chosen: the root is about to become elementary.' },
            { label: 'Set up the integral.', latex: 'L = \\int_{0}^{3}\\sqrt{1 + x}\\;dx' },
            { label: 'Substitute u = 1 + x, with limits 1 and 4.', latex: '= \\int_{1}^{4} u^{1/2}\\,du' },
            { label: 'Power rule.', latex: '= \\left[\\tfrac{2}{3}u^{3/2}\\right]_{1}^{4} = \\tfrac{2}{3}(8 - 1)' },
            { label: 'Evaluate.', latex: 'L = \\frac{14}{3} \\approx 4.67' },
          ]),
          insight('Check it against the straight line. The curve runs from (0, 0) to (3, 2√3) ≈ (3, 3.46), a straight distance of about 4.58. The arc is 4.67 — longer, but not by much, because the curve does not wander far. An answer of 3, or of 12, would have been visibly wrong before any algebra was checked.'),
          warning('Every arc length is at least the straight-line distance between the endpoints, and at most the sum of the horizontal and vertical distances. Two inequalities, ten seconds, and they catch a dropped 1 and a lost square root respectively.', 'Bracket the answer before trusting it'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('arc-length', 4)] },
      independent: { title: 'On your own', blocks: [practice('arc-length', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('arc-length', 3, 'Curves built so the root simplifies, and the bounds every arc length has to satisfy.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'ds = √((dx)² + (dy)²) — a hypotenuse, from Pythagoras on a tiny piece of curve.',
            'For y = f(x): L = ∫√(1 + (f′)²) dx between the two x-values.',
            'The 1 is the horizontal part. Dropping it gives a horizontal line length zero.',
            'The root covers the whole of 1 + (f′)², not just one term.',
            'Almost no curve has an elementary arc length; exercise curves are built so 1 + (f′)² is a perfect square.',
            'The answer is always longer than the straight line between the endpoints.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'surface-of-revolution-01',
    unitId: 'unit-integral-applications',
    conceptId: 'surface-of-revolution',
    title: 'Surface Area of a Solid of Revolution',
    summary: 'Spinning a curve about an axis, and the band of surface that each small piece of arc sweeps out.',
    objectives: [
      'Write the surface-area element as the circumference of a circle times an arc length',
      'Set up and evaluate a surface-area integral about the x-axis and about the y-axis',
      'Distinguish the surface integral from the volume integral it is constantly confused with',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'What a spinning curve sweeps',
        blocks: [
          text('Take the curve y = f(x) between two x-values and spin the whole picture about the x-axis. The curve sweeps out a surface — a vase, a horn, a sphere — and the question is how much material it would take to make it.'),
          text('The pattern is the one from the previous lesson. Cut the curve into pieces short enough to be straight; each piece sweeps out a thin band; add up the bands.'),
          insight('The only thing to work out is the area of one band, and it is easier than it looks. A band is a strip of a cone, and if it is thin enough it is a rectangle bent into a circle: its length is the circumference of the circle it goes round, and its width is the arc length of the piece that made it.'),
        ],
      },
      intuition: {
        title: 'Circumference times arc',
        blocks: [
          text('A point on the curve at height y, spun about the x-axis, traces a circle of radius y. So the band swept by a piece of arc ds at that height has area'),
          math('dS = 2\\pi y\\;ds'),
          text('and ds is exactly what the arc-length lesson produced. Putting the two together, for rotation about the **x-axis**:'),
          math('S = \\int_{a}^{b} 2\\pi f(x)\\sqrt{1 + \\big(f\'(x)\\big)^{2}}\\;dx'),
          text('For rotation about the **y-axis**, nothing changes except which distance is the radius. A point at horizontal position x sweeps a circle of radius x:'),
          math('S = \\int_{a}^{b} 2\\pi x\\sqrt{1 + \\big(f\'(x)\\big)^{2}}\\;dx'),
          insight('The radius is the distance from the point to the axis of rotation, and that is the only thing you have to decide. About the x-axis it is y; about the y-axis it is x; about the line y = 3 it would be |f(x) − 3|. Memorising two formulas hides one idea.'),
          warning('2πy ds is a surface; πy² dx is a volume. They are different quantities with different units, and the confusion is easy to make because the disc formula is the one taught first. The tell is the square root: a surface-area integral always carries the arc element ds, because the surface is made of *arc* swept around, not of *width* swept around.', 'A surface is not a stack of discs'),
          text('**Why not simply 2πy dx?** Because that would be the surface of a stack of cylinders, each with vertical sides, and a slanted piece of curve sweeps out more surface than a vertical one of the same width — exactly as much more as ds exceeds dx. The approximation with dx is wrong even in the limit, and it is wrong in the same way that measuring a staircase by its steps gives the wrong length for the diagonal.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'solid', solid: 'cone', labels: { radius: 'y' } },
            'One band, unrolled. A short piece of arc spun about the axis makes a frustum — a slice of a cone — whose curved surface is its mean circumference times its slant height. In the limit the mean radius is just y and the slant height is ds, which is where 2πy ds comes from.',
          ),
          figure(
            { kind: 'solid', solid: 'sphere', labels: { radius: 'r' } },
            'The sphere is the case worth doing by hand: rotating the upper semicircle y = √(r² − x²) about the x-axis has to give 4πr², and it does — the y in the radius cancels exactly against the y in the denominator of ds, leaving a constant to integrate across a width of 2r.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the surface area swept out by rotating y = √(r² − x²), for −r ≤ x ≤ r, about the x-axis. (This is the sphere, and the answer is known — which is what makes it a good check on the method.)'),
          steps([
            { label: 'Differentiate the semicircle.', latex: 'y = (r^{2} - x^{2})^{1/2} \\quad\\Longrightarrow\\quad y\' = \\frac{-x}{\\sqrt{r^{2} - x^{2}}} = \\frac{-x}{y}' },
            { label: 'Build 1 + (y′)² over a common denominator.', latex: '1 + \\frac{x^{2}}{y^{2}} = \\frac{y^{2} + x^{2}}{y^{2}} = \\frac{r^{2}}{y^{2}}', note: 'because x² + y² = r² on this curve.' },
            { label: 'Take the root — this is ds/dx.', latex: '\\sqrt{1 + (y\')^{2}} = \\frac{r}{y}' },
            { label: 'Assemble the integrand. The y cancels.', latex: '2\\pi y \\cdot \\frac{r}{y} = 2\\pi r' },
            { label: 'Integrate a constant across the width.', latex: 'S = \\int_{-r}^{r} 2\\pi r\\,dx = 2\\pi r\\,(2r)' },
            { label: 'The formula from school geometry, derived.', latex: 'S = 4\\pi r^{2}' },
          ]),
          insight('The cancellation is worth staring at. The band near the equator is wide and short; the band near the pole is narrow and long. Those two effects cancel *exactly*, which is why every horizontal slice of a sphere of equal thickness carries equal surface area — a fact that is not obvious, is genuinely useful, and falls straight out of the integrand being constant.'),
          warning('Had this been set up with πy² instead of 2πy ds, the integral would have given the volume 4πr³/3 — a number, plausible, and an answer to a different question. The units are the check: a surface area must come out proportional to r², and if your answer has an r³ in it you computed a volume.', 'Check the power of r'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('surface-of-revolution', 4)] },
      independent: { title: 'On your own', blocks: [practice('surface-of-revolution', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('surface-of-revolution', 3, 'Rotation about either axis, and telling a surface integral from a volume one.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'dS = 2π × (radius) × ds — a circumference times an arc length.',
            'About the x-axis the radius is y; about the y-axis it is x.',
            'ds always carries the square root √(1 + (f′)²) dx. A surface integral without it is wrong.',
            '2πy ds is surface; πy² dx is volume. Different questions, different units.',
            'The sphere check: the method must give 4πr², and it does.',
            'A surface area scales as a length squared — an r³ in the answer means a volume was computed.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'work-integrals-01',
    unitId: 'unit-integral-applications',
    conceptId: 'work-integrals',
    title: 'Work',
    summary: 'Force times distance, when the force will not hold still — springs, hanging cables and tanks that have to be emptied.',
    objectives: [
      'Set up the work integral ∫F(x) dx for a force that varies with position',
      'Use Hooke\'s law to find a spring constant and the work to stretch a spring',
      'Slice a tank or a cable and write the work each slice contributes',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'When force times distance stops working',
        blocks: [
          text('Physics defines work as force times distance, and for a constant force that is the end of the matter: lifting a 20-newton box by 3 metres is 60 joules.'),
          text('A spring is not like that. The further you stretch it, the harder it pulls back, so there is no single number to call "the force". Neither is a bucket with a hole in it, or a tank of water where the top layer has a short trip and the bottom layer a long one.'),
          insight('The pattern is the one this unit keeps repeating: over a short enough piece of the journey, the force is effectively constant, so the work done on that piece is force times that little distance. Add them up.'),
          math('W = \\int_{a}^{b} F(x)\\,dx'),
        ],
      },
      intuition: {
        title: 'Two shapes of problem',
        blocks: [
          text('**Shape one: the force varies, the object is one thing.** Springs are the standard case. Hooke\'s law says the force needed to hold a spring x units from its natural length is proportional to x:'),
          math('F(x) = kx'),
          text('so the work to stretch it from a to b is'),
          math('W = \\int_{a}^{b} kx\\,dx = \\tfrac{1}{2}k\\big(b^{2} - a^{2}\\big)'),
          warning('x is measured from the spring\'s **natural length**, never from the floor and never from where the last question left it. A spring stretched from 20 cm to 25 cm when its natural length is 18 cm runs from x = 0.02 to x = 0.07, not from 0.20 to 0.25 and not from 0 to 0.05.', 'x is the extension, not the length'),
          text('**Shape two: the object is many things, each moving a different distance.** Pumping a tank, lifting a cable, hauling a leaking bucket. Here there is no F(x) to write down until you decide what a slice is, and the recipe has three parts:'),
          list([
            'Set up a coordinate, and say clearly where its origin is and which way is up.',
            'Take a slice at position y. Find its **weight** — for a liquid, that is density × g × (volume of the slice).',
            'Find the **distance that slice travels**. This is where the variable actually enters.',
          ], true),
          math('dW = (\\text{weight of slice}) \\times (\\text{distance it moves})'),
          insight('Both parts of dW usually depend on y, and they usually depend on it differently. In a cone-shaped tank the slice area shrinks as y falls while the lifting distance grows — which is why the answer is not the total weight times the average depth, and why the integral is doing real work rather than dressing up an arithmetic mean.'),
          warning('The distance a slice moves is measured to wherever it ends up — usually the *top* of the tank or the spout above it, not the top of the liquid and not the bottom of the tank. Getting this backwards produces an answer of exactly the right size and the wrong value, which no plausibility check will catch.', 'Distance to where it is going'),
          tip('Water has a density of about 1000 kg/m³, and weight is mass times g ≈ 9.8 m/s². A cubic metre of water therefore weighs about 9800 newtons. Exercises often give the weight density directly to keep the arithmetic out of the way.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'linear',
              transform: { a: 4 },
              shade: { from: 0, to: 2 },
              min: -1,
              max: 3,
              label: 'F = kx',
            },
            'Work as an area. For a spring the force rises linearly with extension, so the work done — the shaded region — is a triangle: ½ × base × height, which is exactly ½kx². The integral is the general version of that picture for a force graph of any shape.',
          ),
          figure(
            { kind: 'prism', length: 3, width: 2, height: 4, labels: { height: 'depth y' } },
            'A tank, sliced horizontally. Each slab has volume (area) × dy and weight (density × g) × that volume, and each has to be lifted a different distance to reach the top. The integral adds up the slabs; the top ones are nearly free and the bottom ones are expensive.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A force of 30 N holds a spring 0.2 m beyond its natural length. How much work stretches it from its natural length to 0.5 m beyond?'),
          steps([
            { label: 'Find k from the data given.', latex: '30 = k(0.2) \\quad\\Longrightarrow\\quad k = 150\\ \\text{N/m}' },
            { label: 'Set up the work integral, from extension 0 to extension 0.5.', latex: 'W = \\int_{0}^{0.5} 150x\\,dx' },
            { label: 'Integrate.', latex: '= \\Big[75x^{2}\\Big]_{0}^{0.5}' },
            { label: 'Evaluate.', latex: 'W = 75(0.25) = 18.75\\ \\text{J}' },
          ]),
          insight('Check it against the wrong answer everyone reaches for first: force times distance, using the 30 N given, would be 15 J. That underestimates, and it must, because 30 N is the force at 0.2 m and the spring is stiffer than that for most of the journey. Seeing *which way* the naive answer errs is a better check than recomputing.'),
          text('Now a tank. A rectangular tank 2 m by 3 m in plan and 4 m deep is full of water. How much work empties it over the top? Take the weight density of water as ρg = 9800 N/m³, and measure y downwards from the top.'),
          steps([
            { label: 'A slab at depth y has volume 6 dy, so its weight is 9800 × 6 dy.', latex: 'dF = 58800\\,dy' },
            { label: 'It has to be lifted a distance y to reach the top.', latex: 'dW = 58800\\,y\\,dy' },
            { label: 'Add up every slab, from the surface to the bottom.', latex: 'W = \\int_{0}^{4} 58800\\,y\\,dy' },
            { label: 'Integrate.', latex: '= 58800\\left[\\frac{y^{2}}{2}\\right]_{0}^{4} = 58800 \\times 8' },
            { label: 'Evaluate.', latex: 'W = 470{,}400\\ \\text{J}' },
          ]),
          tip('Sanity-check a pumping answer against "total weight lifted from the centre of mass". The tank holds 24 m³, weighing 235,200 N, whose centre is 2 m below the top — giving 470,400 J. The two agree, and they always will for a tank of constant cross-section. When the cross-section varies, that shortcut needs the centre of mass from the next lesson.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('work-integrals', 4)] },
      independent: { title: 'On your own', blocks: [practice('work-integrals', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('work-integrals', 3, 'Springs from measured data, tanks pumped over the rim, and cables hauled up a shaft.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'W = ∫F(x) dx — force times distance, taken one small distance at a time.',
            'Hooke\'s law: F = kx, with x measured from the natural length. Find k from the data first.',
            'Stretching a spring from a to b costs ½k(b² − a²), not ½kb².',
            'For a tank or a cable: weight of a slice, times the distance *that slice* travels.',
            'The distance is to the destination — the rim or the spout — not to the surface of the liquid.',
            'Check a pumping answer against total weight times the depth of the centre of mass.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'center-of-mass-01',
    unitId: 'unit-integral-applications',
    conceptId: 'center-of-mass',
    title: 'Centre of Mass',
    summary: 'The single point a shape balances on, found as an average of position weighted by how much material sits where.',
    objectives: [
      'Compute the centre of mass of a system of point masses',
      'Find the centroid of a region under a curve using the moment integrals',
      'Explain why the vertical coordinate uses half the height of each strip',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Where does it balance',
        blocks: [
          text('Put two children on a see-saw, one twice as heavy as the other, and the pivot has to sit twice as close to the heavy one. That is the whole of this topic; the rest is arithmetic and then an integral.'),
          text('For point masses along a line, the balance point is the average of the positions **weighted by the masses**:'),
          math('\\bar{x} = \\frac{\\sum m_{i}x_{i}}{\\sum m_{i}}'),
          text('The numerator has a name: it is the **moment** about the origin. The denominator is the total mass. Balance point equals moment over mass, and that sentence survives unchanged into the continuous case.'),
          insight('Notice that the centre of mass does not have to be anywhere near any of the material. The centre of mass of a ring is the empty point in the middle, and the centre of mass of a boomerang is off the object entirely. It is an average of positions, not a position of anything.'),
        ],
      },
      intuition: {
        title: 'From a sum to an integral',
        blocks: [
          text('Now take a flat plate — a **lamina** — of uniform density, occupying the region between the curve y = f(x) and the x-axis, from x = a to x = b. Cut it into vertical strips.'),
          text('A strip at position x has height f(x) and width dx, so its area is f(x) dx and — density being uniform and cancelling throughout — that is what stands in for its mass.'),
          text('**The horizontal coordinate** is the easy one. Every point of the strip sits at horizontal position x, so its moment about the y-axis is x · f(x) dx:'),
          math('\\bar{x} = \\frac{1}{A}\\int_{a}^{b} x\\,f(x)\\,dx, \\qquad A = \\int_{a}^{b} f(x)\\,dx'),
          text('**The vertical coordinate** is the one that looks wrong. The points of the strip are *not* all at the same height — they run from 0 up to f(x) — so the strip contributes as though its whole mass sat at its own midpoint, at height f(x)/2:'),
          math('\\bar{y} = \\frac{1}{A}\\int_{a}^{b} \\tfrac{1}{2}\\big(f(x)\\big)^{2}\\,dx'),
          insight('That is where the square comes from, and it is not a rule to memorise: the strip has mass f(x) dx and sits at height f(x)/2, so its moment is the product of the two, which is ½f(x)² dx. Every factor is accounted for.'),
          warning('ȳ is not the average value of f. Averaging f gives the mean height of the curve; the centroid is pulled *below* that, because a tall strip carries more material and all of it is spread between 0 and the top rather than concentrated at the top. For y = x² on [0, 1] the average of f is 1/3 and ȳ is 3/10 — close, different, and the difference is the whole idea.', 'The centroid is not the average height'),
          text('**Symmetry does the work when it is available.** If a region is symmetric about a line, the centroid lies on that line. Half the exercises in this topic are one integral rather than two for exactly that reason, and spotting it is worth more than computing it.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              shade: { from: 0, to: 1 },
              min: -0.5,
              max: 1.5,
              points: [{ x: 0.75, y: 0.3, label: 'centroid' }],
              label: 'y = x²',
            },
            'The region under y = x² from 0 to 1, with its centroid marked at (3/4, 3/10). It sits well to the right, because almost all the area is bunched near x = 1 — and it sits low, because even where the region is tall, the material fills the space beneath the curve rather than sitting on top of it.',
          ),
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: 'b', height: 'h' } },
            'The result worth knowing without integrating: a triangle balances one third of the way up from its base, and on the median from every vertex. Every centroid answer for a triangular region should agree with that, and it is the fastest available check.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the centroid of the region under y = x² between x = 0 and x = 1.'),
          steps([
            { label: 'The area first — it is the denominator of both coordinates.', latex: 'A = \\int_{0}^{1} x^{2}\\,dx = \\left[\\frac{x^{3}}{3}\\right]_{0}^{1} = \\frac{1}{3}' },
            { label: 'Moment about the y-axis: x times the strip mass.', latex: 'M_{y} = \\int_{0}^{1} x \\cdot x^{2}\\,dx = \\left[\\frac{x^{4}}{4}\\right]_{0}^{1} = \\frac{1}{4}' },
            { label: 'Divide.', latex: '\\bar{x} = \\frac{1/4}{1/3} = \\frac{3}{4}' },
            { label: 'Moment about the x-axis: half the height, times the strip mass.', latex: 'M_{x} = \\int_{0}^{1} \\tfrac{1}{2}\\big(x^{2}\\big)^{2}\\,dx = \\tfrac{1}{2}\\left[\\frac{x^{5}}{5}\\right]_{0}^{1} = \\frac{1}{10}' },
            { label: 'Divide by the same area.', latex: '\\bar{y} = \\frac{1/10}{1/3} = \\frac{3}{10}' },
            { label: 'The centroid.', latex: '\\left(\\tfrac{3}{4},\\ \\tfrac{3}{10}\\right)' },
          ]),
          insight('Both coordinates land where the picture says they should. x̄ = 0.75 is to the right of the middle, because the region is thin on the left and thick on the right. ȳ = 0.3 is below half the maximum height of 1, because the region is empty above the curve. A centroid outside the region, or above the highest point of it, is a signal to go back.'),
          tip('The same area appears in both denominators, so compute it once. And if the region is symmetric about a vertical line, x̄ is that line — write it down and integrate only for ȳ.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('center-of-mass', 4)] },
      independent: { title: 'On your own', blocks: [practice('center-of-mass', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('center-of-mass', 3, 'Point masses, centroids of regions under a curve, and the symmetry that removes an integral.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Centre of mass = moment ÷ total mass, for points and for regions alike.',
            'For point masses on a line: x̄ = Σmᵢxᵢ / Σmᵢ.',
            'For a region under y = f(x): x̄ = (1/A)∫x f(x) dx.',
            'ȳ = (1/A)∫½ f(x)² dx — the strip sits at its own midpoint, f(x)/2.',
            'ȳ is not the average value of f, and is lower than it.',
            'A symmetric region has its centroid on the axis of symmetry. Use it and skip an integral.',
          ]),
        ],
      },
    },
  }),
];
